import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import * as Handlebars from 'handlebars/dist/cjs/handlebars';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { WidgetTextTemplate } from 'src/api/models/widget-text-template';
import { WidgetTextTemplateType } from 'src/api/models/widget-text-template-type';
import { WidgetTextTemplateService } from 'src/api/services/widget-text-template.service';

declare const monaco: any;

@Component({
  selector: 'app-widget-text-templates',
  templateUrl: './widget-text-templates.component.html',
  styleUrls: ['./widget-text-templates.component.css']
})
export class WidgetTextTemplatesComponent implements OnInit, OnDestroy {

  @ViewChild('addEditModal') addEditModal: ModalDirective;

  onHtmlEditorInit(editor: any): void {
    editor.addCommand(
      monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF,
      () => this.formatHtml()
    );
  }

  public templates: WidgetTextTemplate[] = [];
  public typeFilter: number | null = 0;

  get filteredTemplates(): WidgetTextTemplate[] {
    if (this.typeFilter === null) return this.templates;
    return this.templates.filter(t => t.type === this.typeFilter);
  }
  public loading = false;
  public saving = false;

  public editingTemplate: WidgetTextTemplate | null = null;
  public isNew = false;
  public previewImageFile: File | null = null;

  public livePreviewHtml = '';
  public previewImageUrls = new Map<number, SafeUrl>();
  private previewObjectUrls = new Map<number, string>();

  constructor(
    private templateService: WidgetTextTemplateService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.previewObjectUrls.forEach(url => URL.revokeObjectURL(url));
  }

  load(): void {
    this.loading = true;
    this.templateService.WidgetTextTemplateGetAll({}).subscribe({
      next: (templates) => {
        this.templates = templates;
        this.loading = false;
        templates.filter(t => t.hasPreviewImage).forEach(t => this.loadPreviewImage(t.id));
      },
      error: () => {
        this.toastr.error('Failed to load templates.');
        this.loading = false;
      }
    });
  }

  private loadPreviewImage(id: number): void {
    this.templateService.WidgetTextTemplateGetPreviewImage(id).subscribe({
      next: (blob) => {
        const existing = this.previewObjectUrls.get(id);
        if (existing) URL.revokeObjectURL(existing);
        const objectUrl = URL.createObjectURL(blob);
        this.previewObjectUrls.set(id, objectUrl);
        this.previewImageUrls.set(id, this.sanitizer.bypassSecurityTrustUrl(objectUrl));
      }
    });
  }

  getPreviewImageUrl(id: number): SafeUrl | null {
    return this.previewImageUrls.get(id) ?? null;
  }

  openAdd(): void {
    this.isNew = true;
    this.previewImageFile = null;
    const now = new Date().toISOString();
    this.editingTemplate = {
      id: 0,
      name: '',
      description: '',
      html: '',
      category: '',
      siteType: null,
      customerId: null,
      isActive: true,
      isSystem: false,
      hasPreviewImage: false,
      sortOrder: 0,
      type: 0 as WidgetTextTemplateType,
      createdAt: now,
      updatedAt: now
    };
    this.livePreviewHtml = '';
    this.addEditModal.show();
  }

  openEdit(template: WidgetTextTemplate): void {
    this.isNew = false;
    this.previewImageFile = null;
    this.editingTemplate = { ...template };
    this.refreshPreview();
    this.addEditModal.show();
  }

  onPreviewImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.previewImageFile = input?.files?.[0] ?? null;
  }

  save(): void {
    if (!this.editingTemplate) return;
    const now = new Date().toISOString();
    this.saving = true;

    const params = {
      previewImage: this.previewImageFile ?? undefined,
      UpdatedAt: now,
      Type: this.editingTemplate.type as 0 | 1,
      SortOrder: this.editingTemplate.sortOrder,
      SiteType: this.editingTemplate.siteType,
      PreviewImageMimeType: this.previewImageFile?.type ?? undefined,
      Name: this.editingTemplate.name,
      IsSystem: this.editingTemplate.isSystem,
      IsActive: this.editingTemplate.isActive,
      Id: this.editingTemplate.id,
      Html: this.editingTemplate.html,
      HasPreviewImage: this.editingTemplate.hasPreviewImage || !!this.previewImageFile,
      Description: this.editingTemplate.description,
      CustomerId: this.editingTemplate.customerId,
      CreatedBy: this.editingTemplate.createdBy,
      CreatedAt: this.editingTemplate.createdAt,
      Category: this.editingTemplate.category
    };

    const op = this.isNew
      ? this.templateService.WidgetTextTemplateCreate(params)
      : this.templateService.WidgetTextTemplateUpdate({ id: this.editingTemplate.id, ...params });

    op.subscribe({
      next: () => {
        this.toastr.success(this.isNew ? 'Template created.' : 'Template updated.');
        this.saving = false;
        this.addEditModal.hide();
        this.load();
      },
      error: () => {
        this.toastr.error('Failed to save template.');
        this.saving = false;
      }
    });
  }

  delete(template: WidgetTextTemplate): void {
    if (!confirm(`Delete template "${template.name}"? This cannot be undone.`)) return;
    this.templateService.WidgetTextTemplateDelete(template.id).subscribe({
      next: () => {
        this.toastr.success('Template deleted.');
        this.load();
      },
      error: () => this.toastr.error('Failed to delete template.')
    });
  }

  refreshPreview(): void {
    if (!this.editingTemplate?.html) {
      this.livePreviewHtml = '';
      return;
    }
    try {
      const compiled = Handlebars.compile(this.editingTemplate.html);
      this.livePreviewHtml = compiled({});
    } catch (e: any) {
      this.livePreviewHtml = `<div class="alert alert-danger"><strong>Template error:</strong> ${e.message}</div>`;
    }
  }

  formatHtml(): void {
    if (!this.editingTemplate?.html) return;
    this.editingTemplate.html = this.beautifyHtml(this.editingTemplate.html);
  }

  private beautifyHtml(html: string): string {
    const INDENT = '  ';
    const voidTags = new Set([
      'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
      'link', 'meta', 'param', 'source', 'track', 'wbr'
    ]);

    // Group 1: Handlebars  Group 2: HTML comments  Group 3: tags  Group 4: text/lone-{
    const tokenRegex = /(\{\{[\s\S]*?\}\})|(<!--[\s\S]*?-->)|(<[^>]*>)|([^<{]+|\{(?!\{))/g;

    interface Token { kind: 'open' | 'close' | 'void' | 'text' | 'hbs-open' | 'hbs-close' | 'hbs'; raw: string; }
    const tokens: Token[] = [];
    let m: RegExpExecArray | null;

    while ((m = tokenRegex.exec(html)) !== null) {
      const raw = m[0];
      if (m[1]) {
        if (/^\{\{#/.test(raw))       tokens.push({ kind: 'hbs-open',  raw });
        else if (/^\{\{\//.test(raw)) tokens.push({ kind: 'hbs-close', raw });
        else                           tokens.push({ kind: 'hbs',        raw });
      } else if (m[2]) {
        // HTML comment — emit as-is at current indent
        tokens.push({ kind: 'text', raw: raw.trim() });
      } else if (m[3]) {
        const tagName = (raw.match(/^<\/?([a-zA-Z][a-zA-Z0-9:]*)/) ?? [])[1]?.toLowerCase() ?? '';
        if (raw.startsWith('</'))                              tokens.push({ kind: 'close', raw });
        else if (raw.endsWith('/>') || voidTags.has(tagName)) tokens.push({ kind: 'void',  raw });
        else                                                   tokens.push({ kind: 'open',  raw });
      } else if (m[4]) {
        const trimmed = raw.trim();
        if (trimmed) tokens.push({ kind: 'text', raw: trimmed });
      }
    }

    let depth = 0;
    const lines: string[] = [];

    for (const tok of tokens) {
      switch (tok.kind) {
        case 'close':
        case 'hbs-close':
          depth = Math.max(0, depth - 1);
          lines.push(INDENT.repeat(depth) + tok.raw);
          break;
        case 'open':
        case 'hbs-open':
          lines.push(INDENT.repeat(depth) + tok.raw);
          depth++;
          break;
        default:
          lines.push(INDENT.repeat(depth) + tok.raw);
      }
    }

    return lines.join('\n');
  }
}

